import React, { useState, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import axios from "axios";
import { Loader2 } from "lucide-react";
import {
  clearAuthSessionCookies,
  clearImpersonationCookies,
  hasImpersonationSession,
  normalizeAccountType,
  normalizeAppRedirectUrl,
  syncAccountTypeCookie,
} from "@/lib/authCookies";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProtectedRoute = ({ children, requiredAccountType = "client" }) => {
  const [authState, setAuthState] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const validateAuth = async () => {
      setIsLoading(true);
      const partnerToken = Cookies.get("token");
      const impersonationToken = Cookies.get("impersonation_token");

      const authToken =
        requiredAccountType === "partner"
          ? partnerToken
          : impersonationToken || partnerToken;

      if (!authToken) {
        setAuthState({ authenticated: false });
        setIsLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${BACKEND_URL}/api/auth/validate`, {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });

        const data = response.data;

        if (data.status === "ok" && data.valid === true) {
          const accountType = normalizeAccountType(data.account_type);
          if (!data.impersonating) {
            syncAccountTypeCookie(accountType, data.bot_type);
          }
          setAuthState({
            authenticated: true,
            accountType,
            impersonating: Boolean(data.impersonating),
            defaultRoute: normalizeAppRedirectUrl(data.default_route),
          });
        } else {
          if (impersonationToken) {
            clearImpersonationCookies();
          } else {
            clearAuthSessionCookies();
          }
          setAuthState({ authenticated: false });
        }
      } catch (error) {
        console.error("Token validation error:", error);
        if (impersonationToken) {
          clearImpersonationCookies();
        } else {
          clearAuthSessionCookies();
        }
        setAuthState({ authenticated: false });
      }

      setIsLoading(false);
    };

    validateAuth();
  }, [location.pathname, requiredAccountType]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0F1115] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-[#00D1FF] animate-spin mx-auto mb-4" />
          <p className="text-[#9CA3AF] font-body text-sm">Verifying authentication...</p>
        </div>
      </div>
    );
  }

  if (!authState?.authenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  const { accountType, defaultRoute, impersonating } = authState;

  if (
    requiredAccountType === "client" &&
    hasImpersonationSession() &&
    location.pathname.startsWith("/dashboard/billing")
  ) {
    return <Navigate to="/dashboard" replace />;
  }

  if (requiredAccountType === "admin") {
    if (accountType !== "admin") {
      return <Navigate to={defaultRoute || "/login"} replace />;
    }
    return children;
  }

  if (accountType === "admin") {
    return <Navigate to={defaultRoute || "/admin"} replace />;
  }

  if (requiredAccountType === "partner" && accountType !== "partner") {
    return <Navigate to={defaultRoute || "/dashboard"} replace />;
  }

  if (requiredAccountType === "client" && accountType === "partner" && !impersonating) {
    return <Navigate to={defaultRoute || "/partner/dashboard"} replace />;
  }

  if (requiredAccountType === "client" && accountType !== "client" && !impersonating) {
    return <Navigate to={defaultRoute || "/login"} replace />;
  }

  return children;
};

export default ProtectedRoute;
